// Geometría de la capa DOM que va encima del lienzo: el mundo está en «píxeles de arte» y la capa en píxeles de
// pantalla (× zoom `z`). PURO salvo `desplazarCercaDelBorde`, que mueve el desplazamiento de la vista.
import type { CSSProperties } from 'react';
import type { Mundo, Rect } from './generar';

/** Caja de pantalla de un rectángulo del mundo (mesa, recepción…). */
export function estiloDe(r: Rect, z: number): CSSProperties {
  return { left: r.x * z, top: r.y * z, width: r.w * z, height: r.h * z };
}

/** Cerca del borde de la vista, el arrastre desplaza la oficina (para llegar a una mesa que no se ve). */
export const BORDE_DESPLAZA_PX = 48;
const PASO_DESPLAZA_PX = 24;

/** Desplaza la vista si el puntero está cerca de su borde. Encima de la lista de la izquierda no; justo a su derecha, hacia la izquierda. */
export function desplazarCercaDelBorde(vista: HTMLElement, clientX: number, clientY: number, panelIzqPx: number): void {
  const r = vista.getBoundingClientRect();
  const dy = clientY < r.top + BORDE_DESPLAZA_PX ? -PASO_DESPLAZA_PX : clientY > r.bottom - BORDE_DESPLAZA_PX ? PASO_DESPLAZA_PX : 0;
  const izq = r.left + panelIzqPx;
  const dx = clientX < izq ? 0 : clientX < izq + BORDE_DESPLAZA_PX ? -PASO_DESPLAZA_PX : clientX > r.right - BORDE_DESPLAZA_PX ? PASO_DESPLAZA_PX : 0;
  if (dx) vista.scrollLeft += dx;
  if (dy) vista.scrollTop += dy;
}

/** La mesa anterior (paso −1) o siguiente (+1) de la sala en su grupo; undefined si no hay. */
export function mesaVecina(m: Mundo, projectId: string, desde: number, paso: 1 | -1, grupoId: string | null = null): number | undefined {
  const reales = m.mesas.filter((x) => x.projectId === projectId && x.grupoId === grupoId).map((x) => x.indice);
  return reales[reales.indexOf(desde) + paso];
}

// ─── Grupos de mesas (04-11, «mesas de equipo») ───
// Un sitio de la escena es una mesa de un grupo («Sin grupo» = null) o la placa entera de un grupo (mesa null = el primer
// sitio libre de ese grupo). El arrastre (useArrastreMesas) lo lleva como una clave de texto.

export interface Sitio {
  grupoId: string | null;
  mesa: number | null;
}

export const claveSitio = (s: Sitio): string => `${s.grupoId ?? ''}#${s.mesa ?? '*'}`;

export function sitioDeClave(clave: string): Sitio {
  const i = clave.lastIndexOf('#');
  const mesa = clave.slice(i + 1);
  return { grupoId: clave.slice(0, i) || null, mesa: mesa === '*' ? null : Number(mesa) };
}

/** El sitio de la sala bajo un punto de la capa (en píxeles de pantalla): una mesa o, si no, la placa de un grupo. */
export function sitioEn(m: Mundo, projectId: string, px: number, py: number): string | null {
  const dentro = enRect(px / m.z, py / m.z);
  const me = m.mesas.find((x) => x.projectId === projectId && dentro(x.rect));
  if (me) return claveSitio({ grupoId: me.grupoId, mesa: me.indice });
  const g = m.grupos.find((x) => x.projectId === projectId && dentro(x.placa));
  return g ? claveSitio({ grupoId: g.grupoId, mesa: null }) : null;
}

/** ¿El punto está sobre OTRA sala? Para decir «cada sala es su proyecto» al soltar ahí (grupos.md regla 12). */
export function otraSalaEn(m: Mundo, projectId: string, px: number, py: number): boolean {
  const dentro = enRect(px / m.z, py / m.z);
  return m.zonas.some((z) => z.projectId !== projectId && dentro({ x: z.rect.x, y: z.letrero.y, w: z.rect.w, h: z.rect.y + z.rect.h - z.letrero.y }));
}

const enRect = (x: number, y: number) => (r: Rect) => x >= r.x && x < r.x + r.w && y >= r.y && y < r.y + r.h;
